import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useSeo } from "../lib/seo";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { ABOUT, CONTACT, CONTACT_EMAIL, PRIVACY } from "../data/infoPages";

type Section = { h: string; p: string[] };

function Sections({ sections, prefix }: { sections: Section[]; prefix: string }) {
  return (
    <div className="space-y-2">
      {sections.map((s, i) => (
        <Panel key={s.h} className="!p-3 sm:!p-4">
          <h2 className="font-comic text-xl text-[#FF5D1F]" data-testid={`${prefix}-section-${i}`}>{s.h}</h2>
          {s.p.map((t) => <p key={t} className="mt-1 text-sm font-semibold leading-relaxed text-[#3b1800] sm:text-base">{t}</p>)}
        </Panel>
      ))}
    </div>
  );
}

const EmailLink = () => <a href={`mailto:${CONTACT_EMAIL}`} className="font-extrabold text-[#FF5D1F] underline" data-testid="contact-email-link">{CONTACT_EMAIL}</a>;

export function AboutPage() {
  useSeo({ title: ABOUT.title, description: ABOUT.description, path: "/about" });
  return (
    <PageShell title={ABOUT.h1} subtitle="Making everyday decisions quick, fair and fun." testId="about-page">
      <Sections sections={ABOUT.sections} prefix="about" />
      <div className="mt-3 flex flex-wrap gap-2">
        <Link to="/"><PopButton tone="orange" data-testid="about-start-button">Start dithering</PopButton></Link>
        <Link to="/contact"><PopButton tone="white" data-testid="about-contact-button">Say hello</PopButton></Link>
        <Link to="/about/paul"><PopButton tone="yellow" data-testid="about-meet-paul-button">Meet Paul</PopButton></Link>
      </div>
    </PageShell>
  );
}

export function PrivacyPage() {
  useSeo({ title: PRIVACY.title, description: PRIVACY.description, path: "/privacy" });
  return (
    <PageShell title={PRIVACY.h1} subtitle={`Plain English, no small print. Last updated ${PRIVACY.updated}.`} testId="privacy-page">
      <Sections sections={PRIVACY.sections} prefix="privacy" />
      <Panel className="mt-2 !p-3 sm:!p-4">
        <h2 className="font-comic text-xl text-[#FF5D1F]">Get in touch</h2>
        <p className="mt-1 text-sm font-semibold sm:text-base">Any privacy question or deletion request: <EmailLink /> or use our <Link to="/contact" className="font-extrabold underline">contact form</Link>.</p>
      </Panel>
    </PageShell>
  );
}

export function ContactPage() {
  useSeo({ title: CONTACT.title, description: CONTACT.description, path: "/contact" });
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — thanks!");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <PageShell title={CONTACT.h1} subtitle={CONTACT.intro} testId="contact-page">
      <Panel className="mb-2 flex items-center gap-2 !p-3">
        <Mail className="h-5 w-5 shrink-0 text-[#FF5D1F]" strokeWidth={3} />
        <p className="text-sm font-semibold sm:text-base">Email us directly: <EmailLink /></p>
      </Panel>
      <Panel className="!p-3 sm:!p-4">
        {sent ? (
          <div data-testid="contact-success">
            <h2 className="font-comic text-2xl text-[#0E7A3E]">Message sent!</h2>
            <p className="mt-1 text-sm font-semibold">Thanks, {form.name.trim()}. We'll get back to you at {form.email} as soon as we can.</p>
            <PopButton size="sm" tone="white" className="mt-2" onClick={() => { setSent(false); setForm({ name: "", email: "", message: "", website: "" }); }} data-testid="contact-send-another">Send another</PopButton>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block text-xs font-extrabold uppercase tracking-wide">Your name
                <input className="field mt-1" required maxLength={80} value={form.name} onChange={set("name")} data-testid="contact-name-input" />
              </label>
              <label className="block text-xs font-extrabold uppercase tracking-wide">Your email
                <input className="field mt-1" type="email" required maxLength={120} value={form.email} onChange={set("email")} data-testid="contact-email-input" />
              </label>
            </div>
            <label className="block text-xs font-extrabold uppercase tracking-wide">Message
              <textarea className="field mt-1 h-36" required minLength={5} maxLength={3000} value={form.message} onChange={set("message")} data-testid="contact-message-input" />
            </label>
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={set("website")} aria-hidden />
            <p className="text-[11px] font-bold text-[#6b3a12]">We only use your details to reply. See our <Link to="/privacy" className="underline">privacy policy</Link>.</p>
            <PopButton type="submit" tone="orange" disabled={busy} data-testid="contact-submit-button"><Send className="h-4 w-4" /> {busy ? "Sending…" : "Send message"}</PopButton>
          </form>
        )}
      </Panel>
    </PageShell>
  );
}
